import React, { useState, useEffect } from 'react';
import { getActivePopup } from '../utils/popupUtils';

const UserPopup = () => {
  const [popup, setPopup] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const loadPopup = async () => {
      try {
        const activePopup = await getActivePopup();
        if (activePopup) {
          setPopup(activePopup);
          // Show popup after a small delay for better UX
          setTimeout(() => {
            setIsVisible(true);
          }, 1000);
        }
      } catch (error) {
        console.error('Error loading popup:', error);
      }
    };

    loadPopup();
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setPopup(null);
    }, 300);
  };

  const handleButtonClick = () => {
    if (popup.buttonLink) {
      window.open(popup.buttonLink, '_blank');
    }
    handleClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!popup || !isVisible) {
    return null;
  }

  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleBackdropClick}
    >
      <div 
        className={`bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={handleClose}
            className="bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* Popup Content */}
        <div className="p-6">
          {/* Image (for image and both types) */}
          {(popup.type === 'image' || popup.type === 'both') && popup.imageUrl && (
            <div className="mb-4">
              <img 
                src={popup.imageUrl} 
                alt={popup.title || "Popup"} 
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>
          )}

          {/* Text Content (for text and both types) */}
          {(popup.type === 'text' || popup.type === 'both') && (
            <div className="text-center space-y-4">
              {popup.title && (
                <h3 className="text-2xl font-bold text-gray-900 leading-tight">
                  {popup.title}
                </h3>
              )}

              {popup.description && (
                <p className="text-gray-600 leading-relaxed">
                  {popup.description}
                </p>
              )}
            </div>
          )}

          {/* Action Button */}
          {popup.buttonText && (
            <div className="mt-6 text-center">
              <button
                onClick={handleButtonClick}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                {popup.buttonText}
              </button>
            </div>
          )}

          {/* No button but image only - add close option */}
          {popup.type === 'image' && !popup.buttonText && (
            <div className="mt-4 text-center">
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPopup;
